import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { buildAgentInstructions } from '@/lib/aiAgentDocs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Key, Plus, Copy, Trash2, Eye, EyeOff, Plug, Bot, Globe } from 'lucide-react';
import { toast } from 'sonner';

const fallbackCopy = (text) => {
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  return ok;
};

const copyText = async (text, label = 'Copied to clipboard') => {
  try { await navigator.clipboard.writeText(text); }
  catch {
    if (!fallbackCopy(text)) { toast.error('Copy blocked by browser — select the text and copy manually'); return; }
  }
  toast.success(label);
};

const mask = (k) => `${k.key_prefix}${'•'.repeat(24)}`;

function KeyRow({ k, onRevoke, onCopyInstructions }) {
  const [show, setShow] = useState(false);
  return (
    <div data-testid={`api-key-${k.id}`} className="border border-gray-200 rounded-lg p-4 space-y-3 hover:border-gray-300 transition-colors">
      <div className="flex items-center gap-3">
        <Key className="h-4 w-4 text-amber-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{k.name}</p>
          <p className="text-[11px] text-gray-400">
            Created {new Date(k.created_at).toLocaleDateString()}
            {k.last_used_at ? ` · Last used ${new Date(k.last_used_at).toLocaleString()}` : ' · Never used'}
          </p>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => onRevoke(k)} data-testid={`revoke-key-${k.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {k.key ? (
        <div className="flex flex-wrap items-center gap-2">
          <code data-testid={`api-key-value-${k.id}`} className="flex-1 min-w-[200px] bg-gray-50 border border-gray-200 rounded px-3 py-2 text-xs font-mono text-gray-900 break-all">
            {show ? k.key : mask(k)}
          </code>
          <Button size="sm" variant="outline" onClick={() => setShow(!show)} data-testid={`toggle-key-${k.id}`}>
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="outline" onClick={() => copyText(k.key, 'API key copied')} data-testid={`copy-key-${k.id}`}>
            <Copy className="h-4 w-4 mr-1" /> Key
          </Button>
          <Button size="sm" onClick={() => onCopyInstructions(k.key)} className="bg-[#0073ea] hover:bg-[#0060c2] text-white" data-testid={`copy-instructions-${k.id}`}>
            <Bot className="h-4 w-4 mr-1" /> Copy AI Instructions
          </Button>
        </div>
      ) : (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          This older key can't be shown again. Delete it and create a new one to get a copyable key.
        </p>
      )}
    </div>
  );
}

export default function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const baseUrl = window.location.origin;

  const load = async () => {
    try { setKeys((await API.get('/api-keys')).data); } catch { toast.error('Failed to load keys'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error('Give the key a name (e.g. Claude)'); return; }
    setCreating(true);
    try {
      await API.post('/api-keys', { name: name.trim() });
      setName('');
      await load();
      toast.success('API key created');
    } catch { toast.error('Failed to create key'); }
    finally { setCreating(false); }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Delete key "${k.name}"? Any AI agent using it will lose access.`)) return;
    try {
      await API.delete(`/api-keys/${k.id}`);
      setKeys(prev => prev.filter(x => x.id !== k.id));
      toast.success('API key deleted');
    } catch { toast.error('Failed to delete key'); }
  };

  const copyInstructions = (key) => copyText(buildAgentInstructions(baseUrl, key), 'AI agent instructions copied — paste them into your AI');

  return (
    <div data-testid="api-keys-page" className="max-w-4xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#0073ea]/10 flex items-center justify-center">
          <Plug className="h-5 w-5 text-[#0073ea]" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">API Keys & Integrations</h1>
          <p className="text-sm text-gray-500">Give Claude, ChatGPT or any AI agent full access to your CRM.</p>
        </div>
      </div>

      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-6 space-y-3">
          <p className="text-sm font-semibold text-gray-900">How it works</p>
          <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
            <li>Create a key below (name it after the AI you'll use, e.g. "Claude").</li>
            <li>Click <span className="font-semibold text-[#0073ea]">Copy AI Instructions</span> — it copies your key, your website URL and the full API guide.</li>
            <li>Paste it into your AI agent's chat or project instructions. Done — it can now create leads, generate contracts, send emails and more.</li>
          </ol>
          <div className="flex flex-wrap items-center gap-2 pt-2 text-xs text-gray-500">
            <Globe className="h-3.5 w-3.5" /> Your API URL:
            <code data-testid="api-base-url" className="font-mono text-gray-800 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">{baseUrl}/api</code>
            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => copyText(`${baseUrl}/api`, 'URL copied')} data-testid="copy-base-url">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-gray-200 shadow-sm" data-testid="api-keys-section">
        <CardContent className="p-6 space-y-4">
          <p className="text-sm font-semibold text-gray-900">Your API Keys</p>
          <div className="flex gap-2">
            <Input data-testid="new-key-name" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()}
              placeholder="Key name (e.g. Claude)" className="flex-1 bg-white border-gray-300" />
            <Button data-testid="create-key-btn" onClick={create} disabled={creating} className="bg-[#0073ea] hover:bg-[#0060c2] text-white">
              <Plus className="h-4 w-4 mr-1" /> Create Key
            </Button>
          </div>
          {loading ? <div className="h-20 bg-gray-100 rounded-lg animate-pulse" /> : keys.length === 0 ? (
            <p data-testid="no-keys-msg" className="text-sm text-gray-400 text-center py-6">No API keys yet. Create one to connect an AI agent.</p>
          ) : (
            <div className="space-y-3">{keys.map(k => <KeyRow key={k.id} k={k} onRevoke={revoke} onCopyInstructions={copyInstructions} />)}</div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-6 space-y-3">
          <p className="text-sm font-semibold text-gray-900">AI Agent Instructions Preview</p>
          <pre data-testid="instructions-preview" className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-[11px] font-mono text-gray-700 whitespace-pre-wrap max-h-96 overflow-y-auto">
            {buildAgentInstructions(baseUrl, 'YOUR_API_KEY')}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
